import React, { useState, useCallback, useMemo, useRef } from 'react';
import StoreManager    from './StoreManager';
import ScanStatus      from './ScanStatus';
import ArbitragePanel  from './ArbitragePanel';
import ResultsTable    from './ResultsTable';
import BrandFilter, { extractBrands, isGeneric } from './BrandFilter';
import ImageSearch from './ImageSearch';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';
import HealthcareSearch from './components/HealthcareSearch';
import CostCheck from './components/CostCheck';
import FooterPage, { FOOTER_LINKS, PrivacyPageContent, TermsPageContent } from './components/FooterPages';
import TravelSearch from './components/TravelSearch';
import BillsSearch from './components/BillsSearch';
import AutoTab from "./components/AutoTab";
import EnrollmentAdvisor from './components/EnrollmentAdvisor';
import PayerIntelligence  from './components/PayerIntelligence';
import ProviderIntelligence from './components/ProviderIntelligence';
import EnrollmentIntelligence from './components/EnrollmentIntelligence';
import { useAuth } from './AuthContext';
import {
  DEFAULT_STORES, EXTRA_COLORS, hex2rgba,
  simulateAgent, computeResults, KNOWN_STORES, getRelevantStores,
} from './data';
import styles from './App.module.css';

const DEFAULT_SUGGESTIONS = [
  'wireless headphones', 'air fryer', 'power drill',
  'coffee maker', 'robot vacuum', 'smart TV', 'bluetooth speaker',
];

const HISTORY_KEY  = 'mioprice_search_history';
const MAX_HISTORY  = 7;

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToHistory(query) {
  try {
    const prev    = loadHistory();
    const updated = [query, ...prev.filter(q => q.toLowerCase() !== query.toLowerCase())].slice(0, MAX_HISTORY);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
}

// MVP: Healthcare-only. Other tabs preserved for future reactivation.
// { key: 'shopping', label: 'Shopping', icon: '🛍️' },
// { key: 'travel',   label: 'Travel',   icon: '✈️' },
// { key: 'bills',    label: 'Bills',    icon: '🧾' },
// { key: 'auto',     label: 'Auto',     icon: '🚗' },
const TABS = [
  { key: 'healthcare', label: 'Cost Navigator', icon: '🔍' },
  { key: 'costcheck',  label: 'Cost Check',     icon: '💰' },
  { key: 'enroll',     label: 'Enroll',         icon: '📋' },
  { key: 'payer',      label: 'Payer Intelligence',    icon: '📊', requiresPayer: true },
  { key: 'provider',   label: 'Provider Intelligence',  icon: '📈', requiresPayer: true },
  { key: 'employer',   label: 'Employer',               icon: '🏢', requiresPayer: true },
];

let extraColorIndex = 0;

export default function App() {
  const { user, isPayer, isSupabaseEnabled } = useAuth();
  const [showAuth, setShowAuth]             = useState(false);
  const [footerPage, setFooterPage]         = useState(null);
  const [activeTab, setActiveTab]           = useState('healthcare');
  const [stores, setStores]                 = useState(DEFAULT_STORES.map((s) => ({ ...s, active: true })));
  const [query, setQuery]                   = useState('');
  const [running, setRunning]               = useState(false);
  const [searched, setSearched]             = useState(false);
  const [agentStatus, setAgentStatus]       = useState({});
  const [agentData, setAgentData]           = useState({});
  const [ranked, setRanked]                 = useState([]);
  const [arbitrage, setArbitrage]           = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [searchHistory, setSearchHistory]   = useState(() => {
    const h = loadHistory();
    return h.length > 0 ? h : DEFAULT_SUGGESTIONS;
  });

  const allDataRef = useRef({});

  const storeMap = useMemo(
    () => Object.fromEntries(stores.map((s) => [s.id, s])),
    [stores]
  );

  const activeStores = stores.filter((s) => s.active);

  // ── Run search ────────────────────────────────────────────────
  const runSearch = useCallback(async (q) => {
    const searchQuery = (q || query).trim();
    if (!searchQuery || running || !activeStores.length) return;

    setRunning(true);
    setSearched(true);
    setRanked([]);
    setArbitrage([]);
    setSelectedBrands([]);
    setSearchHistory(saveToHistory(searchQuery));
    if (q) setQuery(q);

    const relevantStores = getRelevantStores(searchQuery, activeStores);
    const skippedStores  = activeStores.filter(s => !relevantStores.find(r => r.id === s.id));

    const initStatus = {};
    const initData   = {};
    activeStores.forEach((s) => {
      const isSkipped = skippedStores.find(sk => sk.id === s.id);
      initStatus[s.id] = isSkipped ? 'skipped' : 'loading';
      initData[s.id]   = [];
    });
    setAgentStatus(initStatus);
    setAgentData(initData);

    allDataRef.current = {};

    await Promise.allSettled(
      relevantStores.map(async (store) => {
        try {
          const items = await simulateAgent(store, searchQuery);
          allDataRef.current[store.id] = items;
          setAgentData((prev) => ({ ...prev, [store.id]: items }));
          setAgentStatus((prev) => ({ ...prev, [store.id]: 'done' }));
          const { ranked: r, arbitrage: a } = computeResults(allDataRef.current, activeStores);
          setRanked(r);
          setArbitrage(a);
        } catch (err) {
          setAgentStatus((prev) => ({ ...prev, [store.id]: 'error' }));
          allDataRef.current[store.id] = [];
        }
      })
    );

    setRunning(false);
  }, [query, running, activeStores]);

  // ── Store management ──────────────────────────────────────────
  const handleToggleStore = (id) => {
    setStores((prev) => prev.map((s) => s.id === id ? { ...s, active: !s.active } : s));
  };

  const handleRemoveStore = (id) => {
    setStores((prev) => prev.filter((s) => s.id !== id));
  };

  const handleAddStore = (name, storeObj) => {
    const id = storeObj?.id || name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (stores.find((s) => s.id === id)) return;
    const color = storeObj?.color || EXTRA_COLORS[extraColorIndex++ % EXTRA_COLORS.length];
    setStores((prev) => [
      ...prev,
      {
        id,
        name: storeObj?.name || name,
        color,
        bg: storeObj?.bg || hex2rgba(color, 0.1),
        official: storeObj?.official ?? false,
        active: true,
      },
    ]);
  };

  const brands         = extractBrands(ranked);
  const filteredRanked = selectedBrands.length === 0
    ? ranked
    : ranked.filter(r => {
        if (selectedBrands.includes('__others__') && isGeneric(r.brand)) return true;
        return selectedBrands.includes(r.brand);
      });

  const showTable = running || ranked.length > 0;

  // Standalone /terms route for Google OAuth branding verification
  if (typeof window !== 'undefined' && window.location.pathname === '/terms') {
    return (
      <div style={{ maxWidth:800, margin:'0 auto', padding:'40px 24px', fontFamily:'system-ui,sans-serif' }}>
        <div style={{ marginBottom:32 }}>
          <span style={{ fontSize:22, fontWeight:800, color:'#2563eb' }}>Mio</span>
          <span style={{ fontSize:22, fontWeight:800, color:'#111827' }}>Price</span>
          <span style={{ fontSize:13, color:'#6b7280', marginLeft:12 }}>Terms &amp; Conditions</span>
        </div>
        <TermsPageContent />
        <div style={{ marginTop:40, paddingTop:16, borderTop:'1px solid #e5e7eb', fontSize:12, color:'#9ca3af' }}>
          <a href='/' style={{ color:'#2563eb', textDecoration:'none' }}>← Back to MioPrice</a>
        </div>
      </div>
    );
  }

  // Standalone /privacy route for Google OAuth branding verification
  if (typeof window !== 'undefined' && window.location.pathname === '/privacy') {
    return (
      <div style={{ maxWidth:800, margin:'0 auto', padding:'40px 24px', fontFamily:'system-ui,sans-serif' }}>
        <div style={{ marginBottom:32 }}>
          <span style={{ fontSize:22, fontWeight:800, color:'#2563eb' }}>Mio</span>
          <span style={{ fontSize:22, fontWeight:800, color:'#111827' }}>Price</span>
          <span style={{ fontSize:13, color:'#6b7280', marginLeft:12 }}>Privacy Policy</span>
        </div>
        <PrivacyPageContent />
        <div style={{ marginTop:40, paddingTop:16, borderTop:'1px solid #e5e7eb', fontSize:12, color:'#9ca3af' }}>
          <a href='/' style={{ color:'#2563eb', textDecoration:'none' }}>← Back to MioPrice</a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Header ── */}
        <div className={styles.header}>

          {/* Row 1: logo (left) + auth (right), vertically aligned */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h1 className={styles.title}>
                Mio<span className={styles.accent}>Price</span>
              </h1>
              <p className={styles.subtitle}>
                Consumer Healthcare Pricing Intelligence Platform
              </p>
            </div>

            {/* ── Auth ── */}
            {isSupabaseEnabled && (
              user
                ? <UserMenu />
                : <button
                    onClick={() => setShowAuth(true)}
                    style={{
                      background: '#2563eb', color: '#fff', border: 'none',
                      borderRadius: 8, padding: '8px 16px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                    Sign in
                  </button>
            )}
          </div>

          {/* ── Ticker banner ── */}
          <div style={{ overflow:'hidden', background:'#1a2332', borderRadius:6,
            margin:'6px 0', padding:'5px 0' }}>
            <div style={{
              display:'inline-block',
              whiteSpace:'nowrap',
              animation:'mioprice-ticker 28s linear infinite',
              fontSize:11, color:'#e2e8f0', fontWeight:500, letterSpacing:0.2
            }}>
              &nbsp;&nbsp;&nbsp;✅ <strong style={{color:'#3b82f6'}}>Live:</strong> Illinois · California · UnitedHealthcare · Aetna · Cigna · 737 procedures · 84,000+ providers&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;🔄 <strong style={{color:'#94a3b8'}}>Expanding:</strong> All major payers · All 50 states · More procedures coming soon&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </div>
            <style>{`@keyframes mioprice-ticker { 0%{transform:translateX(100vw)} 100%{transform:translateX(-100%)} }`}</style>
          </div>

          {/* Row 2: category tabs — scrollable on narrow screens */}
          <div style={{
            display: 'flex', gap: 3,
            background: '#f3f4f6',
            borderRadius: 10,
            padding: 3,
            overflowX: 'auto',
            maxWidth: '100%',
          }}>
            {TABS.filter(tab => !tab.requiresPayer || isPayer).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  background: activeTab === tab.key ? '#fff' : 'transparent',
                  color: activeTab === tab.key ? '#2563eb' : '#6b7280',
                  boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 13 }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

        </div>

        {/* ── Auth modal ── */}
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

        {/* ════════════════════════════════════════════
            HEALTHCARE TAB
        ════════════════════════════════════════════ */}
        {activeTab === 'healthcare' && !footerPage && (
          <HealthcareSearch embedded />
        )}

        {activeTab === 'costcheck' && !footerPage && (
          <CostCheck />
        )}

        {activeTab === 'enroll' && !footerPage && (
          <EnrollmentAdvisor />
        )}

        {activeTab === 'provider' && !footerPage && (
          isPayer
            ? <ProviderIntelligence />
            : <div style={{padding:40,textAlign:'center',color:'#6b7280'}}>Provider Intelligence requires a subscription.</div>
        )}
        {activeTab === 'employer' && !footerPage && (
          isPayer
            ? <EnrollmentIntelligence />
            : <div style={{padding:40,textAlign:'center',color:'#6b7280'}}>Employer Intelligence requires a subscription.</div>
        )}
        {activeTab === 'payer' && !footerPage && (
          isPayer
            ? <PayerIntelligence />
            : <div style={{ maxWidth: 600, margin: '60px auto', textAlign: 'center', padding: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Payer Intelligence</h2>
                <p style={{ color: '#6b7280', fontSize: 14 }}>This is a restricted B2B workspace. Please sign in with a payer account to continue.</p>
              </div>
        )}

        {footerPage && (
          <FooterPage page={footerPage} onClose={() => setFooterPage(null)} />
        )}

        {/* ════════════════════════════════════════════
            TRAVEL TAB
        ════════════════════════════════════════════ */}
        {activeTab === 'travel' && (
          <TravelSearch />
        )}

        {/* ════════════════════════════════════════════
            BILLS TAB
        ════════════════════════════════════════════ */}
        {activeTab === 'bills' && (
          <BillsSearch />
        )}

         {/* ════════════════════════════════════════════
            AUTO TAB
        ════════════════════════════════════════════ */}
        {activeTab === 'auto' && (
          <AutoTab />
        )}

        {/* ════════════════════════════════════════════
            SHOPPING TAB — existing MioPrice UI
        ════════════════════════════════════════════ */}
        {activeTab === 'shopping' && (
          <>
            {/* ── Search ── */}
            <div className={styles.searchRow}>
              <div className={styles.searchWrap}>
                <span className={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  placeholder="Search any product — headphones, drill, coffee maker, TV…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  disabled={running}
                  className={styles.searchInput}
                />
              </div>
              <button
                className={`${styles.searchBtn} primary`}
                onClick={() => runSearch()}
                disabled={running || !query.trim()}
              >
                {running ? 'Scanning…' : 'Save Money Today'}
              </button>
              <ImageSearch
                disabled={running}
                onQuery={(q) => {
                  setQuery(q);
                  runSearch(q);
                }}
              />
            </div>

            {/* ── Suggestions ── */}
            <div className={styles.suggestions}>
              <span className={styles.sugLabel}>
                {loadHistory().length > 0 ? '🕐 Recent:' : 'Try:'}
              </span>
              {searchHistory.map((s) => (
                <button
                  key={s}
                  className={styles.sug}
                  onClick={() => runSearch(s)}
                  disabled={running}
                >
                  {s}
                </button>
              ))}
              {loadHistory().length > 0 && (
                <button
                  className={styles.sugClear}
                  onClick={() => {
                    localStorage.removeItem(HISTORY_KEY);
                    setSearchHistory(DEFAULT_SUGGESTIONS);
                  }}
                  disabled={running}
                >
                  Clear history
                </button>
              )}
            </div>

            {/* ── Store manager ── */}
            <StoreManager
              stores={stores}
              onToggle={handleToggleStore}
              onRemove={handleRemoveStore}
              onAdd={handleAddStore}
            />

            {/* ── Scan status ── */}
            {running && (
              <ScanStatus
                stores={activeStores}
                agentStatus={agentStatus}
                agentData={agentData}
              />
            )}

            {/* ── Brand filter ── */}
            {ranked.length > 0 && (
              <BrandFilter
                brands={brands}
                selected={selectedBrands}
                onChange={setSelectedBrands}
              />
            )}

            {/* ── Arbitrage panel ── */}
            <ArbitragePanel arbitrage={arbitrage} storeMap={storeMap} />

            {/* ── Results table ── */}
            {showTable && (
              <ResultsTable
                ranked={filteredRanked}
                storeMap={storeMap}
                loading={running}
                activeCount={activeStores.length}
              />
            )}

            {/* ── Empty state ── */}
            {!searched && (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>🛍️</div>
                <p>Search a product to compare prices across all marketplaces</p>
                <p className={styles.emptyHint}>
                  Agents run in parallel · Results rank by price · Arbitrage auto-detected
                </p>
              </div>
            )}
          </>
        )}

      {/* Footer */}
      {!footerPage && (
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 32px', display: 'flex', gap: 16, flexWrap: 'wrap', background: '#fff', marginTop: 'auto' }}>
          {FOOTER_LINKS.map(l => (
            l.key === 'privacy' ? (
              <a key={l.key} href="/privacy" style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'none' }}>
                {l.label}
              </a>
            ) : l.key === 'terms' ? (
              <a key={l.key} href="/terms" style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'none' }}>
                {l.label}
              </a>
            ) : (
              <span key={l.key} onClick={() => setFooterPage(l.key)} style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
                {l.label}
              </span>
            )
          ))}
          <span style={{ fontSize: 12, color: '#d1d5db', marginLeft: 'auto' }}>© 2026 MyAtrix LLC · mioprice.com</span>
        </div>
      )}

      </div>
    </div>
  );
}
